import { useEffect, useState } from "react";
import { connect, mapProps, useForm } from "@formily/react";
import { IServicePartner } from "@rumble-it/common-lib/src/types/ServicePartner";
import { scrollToElement } from "@rumble-it/common-lib/src/helpers/scrollTo";
import { fetchServicePartnerSelector } from "@rumble-it/common-lib/src/api/ServicePartner";
import { fetchDealershipSelector } from "@rumble-it/common-lib/src/api/Dealership";
import { useStateMachine } from "little-state-machine";
import { Button, IRumbleFormScope } from ".";
import { IFormStep } from "@formily/antd-v5";
import { ServicePartnerCard } from "./ServicePartner";
import { DealershipCard } from "./Dealership";
import { IDealership } from "@rumble-it/common-lib/src/types/Dealership";

export const LocationSelector = (props: any) => {
  const form = useForm();
  const { state } = useStateMachine();
  const [locations, setLocations] = useState([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showAllLocations, setShowAllLocations] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<
    IServicePartner | IDealership | null
  >(props.value || null);

  // Safely extract scope and fetchData
  const scope = (state as { scope?: IRumbleFormScope }).scope;
  const fetchData = scope?.fetchData;
  if (!fetchData) {
    throw new Error("fetchData function is not available");
  }
  const formStep = scope?.formStep as IFormStep;
  if (!formStep) {
    throw new Error("formStep function is not available");
  }
  const scrollTo = props.scrollTo ?? true;

  // Get values from form
  const postalCode = form.values.postalCode;
  const streetNumber = form.values.streetNumber;

  if (!postalCode || !streetNumber) {
    return;
  }

  useEffect(() => {
    async function fetchLocations() {
      setLoading(true);
      setError(null);

      try {
        const [fetchedDealerships, fetchedServicePartners] = await Promise.all([
          fetchDealershipSelector({
            postalCode,
            streetNumber,
            fetchData,
          }),
          fetchServicePartnerSelector({
            postalCode,
            streetNumber,
            fetchData,
          }),
        ]);

        const combined = [...fetchedDealerships, ...fetchedServicePartners];

        // Add locationIri property to each location based on its type
        const locationsWithIri = combined.map((location) => {
          if (location["@type"] === "ServicePartnerSelector") {
            return {
              ...location,
              locationIri: location.servicePartnerIri || location["@id"],
            };
          } else {
            return {
              ...location,
              locationIri: location.dealershipIri || location["@id"],
            };
          }
        });

        locationsWithIri.sort(
          (a, b) => a.travelTimeInMinutes - b.travelTimeInMinutes,
        );

        setLocations(locationsWithIri);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    }

    fetchLocations();
  }, [postalCode, streetNumber, fetchData]);

  const handleSelectServicePartner = (
    servicePartner: IServicePartner | IDealership,
  ) => {
    form.setValues({
      ...form.values,
      servicePartner: servicePartner,
    });
    setSelectedLocation(servicePartner);

    props.onChange(servicePartner);

    if (formStep) {
      formStep.next();
      if (scrollTo !== false) {
        scrollToElement({
          element: scrollTo === true ? "#rumbledirect-form" : scrollTo,
        });
      }
    }
  };

  const handleSelectDealership = (dealership: IDealership) => {
    const values = {
      ...form.values,
      dealership: dealership,
    };
    if (dealership.servicePartners && dealership.servicePartners[0]) {
      values.servicePartner = {
        servicePartnerIri: dealership.servicePartners[0],
      };
    }

    form.setValues(values);
    setSelectedLocation(dealership);

    props.onChange(dealership);

    if (formStep) {
      formStep.next();
      if (scrollTo !== false) {
        scrollToElement({
          element: scrollTo === true ? "#rumbledirect-form" : scrollTo,
        });
      }
    }
  };

  const toggleServicePartners = () => {
    setShowAllLocations(!showAllLocations);
  };

  // Determine which locations to show
  const visibleLocations = () => {
    if (locations.length === 0) return [];
    if (locations.length === 1) return locations;

    return showAllLocations ? locations : [locations[0]];
  };

  // Calculate how many locations are hidden
  const hiddenLocationsCount = locations.length > 1 ? locations.length - 1 : 0;

  return (
    <div className="formily-service-partner-selector">
      {loading ? (
        <div className="text-center text-red-500 p-4">
          <p>
            Er is wat mis gegaan bij het zoeken naar vestigingen. Controleer het
            ingevoerde adres en probeer het opnieuw.
          </p>
        </div>
      ) : null}

      {error ? (
        <div className="text-center text-red-500 p-4">
          <p>{error}</p>
        </div>
      ) : null}

      {!loading && !error && locations.length === 0 ? (
        <div className="text-center p-4">
          <p>Geen locaties gevonden voor deze locatie.</p>
        </div>
      ) : null}

      {!loading && locations.length > 0 ? (
        <div className="space-y-4">
          {visibleLocations().map((location) => {
            const isServicePartner =
              location["@type"] === "ServicePartnerSelector";
            const isSelected =
              selectedLocation &&
              "locationIri" in location &&
              "locationIri" in selectedLocation &&
              location.locationIri === selectedLocation.locationIri;

            return isServicePartner ? (
              <ServicePartnerCard
                key={location["@id"]}
                servicePartner={location}
                isSelected={isSelected}
                onClick={handleSelectServicePartner}
              />
            ) : (
              <DealershipCard
                key={location["@id"]}
                dealership={location as IDealership}
                isSelected={isSelected}
                onClick={handleSelectDealership}
              />
            );
          })}

          {hiddenLocationsCount > 0 ? (
            <div className="text-center">
              <Button
                variant="outlined"
                className="font-bold text-sm tracking-normal"
                onClick={toggleServicePartners}
              >
                {showAllLocations
                  ? `${hiddenLocationsCount} vestigingen verbergen`
                  : `nog ${hiddenLocationsCount} weergeven`}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {props.errors?.length > 0 ? (
        <div className="text-red-500 p-4">
          {props.errors.map((error, index) => (
            <div key={index}>{error.messages.join(", ")}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export const RumbleLocationSelector = connect(
  LocationSelector,
  mapProps((props: any, field: any) => {
    return {
      ...props,
      value: props.value,
      onChange: props.onChange,
      invalid: field?.valid === false,
      errors: field?.errors,
    };
  }),
);

export default LocationSelector;
